import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import SEO from '../../components/SEO'
import FAQSection from '../../components/FAQSection'

export default function BwindiBirdwatching() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <div>
      <SEO
        title="Bwindi Birdwatching Safaris – 24 Albertine Endemics | 2025 Birding Tours"
        description="Track 24 Albertine Rift endemic species in Bwindi's montane rainforest. Expert-guided birding safaris to Mubwindi Swamp and Ruhija sector. African Green Broadbill, Shelley's Crimsonwing, and 350+ species. Book your 2025 Uganda birding adventure."
      />
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1452570053594-1b985d6ea890?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Bwindi Birdwatching Safaris – 2025
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Track 24 Albertine Rift endemics in Africa's premier montane birding forest
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(87 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-eye" className="w-4 h-4 mr-2" />
                350+ Species
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-star" className="w-4 h-4 mr-2" />
                24 Endemics
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                UNESCO Site
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Birding Safari
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Africa's Premier Montane Forest Birding Destination
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  While most visitors come to Bwindi Impenetrable Forest for mountain gorillas, serious birders know this ancient UNESCO World Heritage Site harbors something equally spectacular: one of Africa's highest concentrations of Albertine Rift endemic species. With over 350 recorded bird species—including an extraordinary 24 Albertine Rift endemics (90% of all species in this category found in East Africa)—Bwindi ranks among the continent's most important birding destinations. Fourteen of these species are found nowhere else in Uganda, making your visit here absolutely essential for serious listers.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Your Premier Birding Sectors & Trails</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Bwindi's four sectors—Buhoma, Ruhija, Rushaga, and Nkuringo—each offer distinct birding opportunities across dramatically different elevations (1,160m to 2,607m). This elevation range creates remarkable habitat diversity, from lowland rainforest specialists to afromontane endemics. The forest itself dates back over 25,000 years, making it one of Africa's oldest ecosystems and a critical refuge for species found nowhere else on Earth.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  <strong>Ruhija Sector</strong> in the eastern highlands stands as Bwindi's birding crown jewel. The legendary <strong>Mubwindi Swamp Trail</strong>—challenging but extraordinarily rewarding—is your best bet for the elusive African Green Broadbill (Grauer's Broadbill), which calls here during its breeding season from late May to early October. Listen for its distinctive ascending "sisisiiii" call in the dense vegetation. The Ruhija Nature Trail and Bamboo Zone add Rwenzori Nightjar, Handsome Francolin, African Wood Owl, and exceptional views of Regal Sunbirds and Cinnamon-chested Bee-eaters.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  <strong>Buhoma Sector</strong> in the north offers easier access and excellent diversity. The Buhoma Waterfall Trail delivers sightings of African Emerald Cuckoo, Red-headed Bluebill, and White-starred Robin-Chat among stunning forest scenery. Additional trails like the Ivy River Trail, Rushura Hill Trail, Muzabajiro Loop, and Munyaga River Trail each provide unique birding opportunities with cooperative species and beautiful settings perfect for photography.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  <strong>Rushaga Sector</strong> in the south attracts target species like Yellow-eyed Black Flycatcher and Cassin's Flycatcher. "The Neck," a narrow riverine forest corridor between Ruhija and Buhoma along the main road, offers unique half-day birding with different habitat specialists.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">The Albertine Rift Endemic Experience</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Your birding adventure begins in the pre-dawn darkness, meeting your specialized bird guide—expertly trained to recognize species by both sight and sound, essential in this dense canopy forest. As you enter the forest at first light, the cacophony of calls is mesmerizing: Great Blue Turacos booming from the canopy, Black-billed Turacos responding, Rwenzori Batises calling, and dozens more voices creating an unforgettable dawn chorus.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  On a full-day expedition with an experienced guide, you can realistically expect to encounter 8-15 Albertine Rift endemics, with multi-day trips significantly increasing your endemic count. Target species include the stunning (but well-camouflaged) African Green Broadbill, the rare Shelley's Crimsonwing (occasionally seen in bamboo zones feeding on grass seeds), Grauer's Rush Warbler, Chapin's Flycatcher, Mountain Masked Apalis, Collared Apalis, Blue-headed Sunbird, Purple-breasted Sunbird, Dusky Crimsonwing, Stripe-breasted Tit, Strange Weaver, and many more. Whether you're a hardcore lister chasing every endemic or a casual enthusiast marveling at your first turacos, Bwindi delivers world-class avian experiences you'll remember forever.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Birding Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-star', title: '24 Albertine Rift Endemics', description: '90% of all Albertine endemics in East Africa—including African Green Broadbill and Grauer\'s Rush Warbler' },
                  { icon: 'fa-eye', title: '350+ Species Recorded', description: 'One of Africa\'s highest bird diversity levels with 14 species found nowhere else in Uganda' },
                  { icon: 'fa-users', title: 'Expert Birding Guides', description: 'UWA-licensed specialists trained in visual and audio identification of endemic species' },
                  { icon: 'fa-mountain', title: 'Elevation Diversity', description: '1,160m to 2,607m range creates varied habitats from lowland to montane specialists' },
                  { icon: 'fa-map-marked', title: 'Premier Trails', description: 'Mubwindi Swamp, Buhoma Waterfall, and Ruhija trails—each with unique target species' },
                  { icon: 'fa-calendar', title: 'Year-Round Birding', description: 'Resident endemics always present; migratory species November-April add extra diversity' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Full-Day Birding Safari Timeline
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your target species,
                  preferred sector (Ruhija, Buhoma, Rushaga), fitness level, and whether you want a half-day or full-day experience.
                  We can adjust locations, timing, and trails to maximize your endemic sightings.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '05:30 AM', title: 'Pre-Dawn Preparation', desc: 'Meet your expert UWA-licensed birding guide for a briefing on target species and trail selection. Discuss which Albertine endemics are your priorities—African Green Broadbill, Shelley\'s Crimsonwing, specific warblers, or sunbirds.' },
                  { time: '06:00 AM', title: 'Dawn Chorus Begins', desc: 'Enter the forest as the first light breaks. The dawn chorus is spectacular—turacos calling from the canopy, barbets drumming, and apalises chattering. This is prime time for audio detection of elusive species.' },
                  { time: '06:30 AM', title: 'Canopy Birding', desc: 'Work fruiting trees for Great Blue and Black-billed Turacos, sunbirds (Regal, Blue-headed, Purple-breasted), and tinkerbirds. Your guide uses their spotting scope to bring distant canopy species into crystal-clear view.' },
                  { time: '08:00 AM', title: 'Mixed Feeding Flocks', desc: 'Follow roving mixed-species flocks that can include 10-20 species together: Mountain Masked Apalis, Collared Apalis, warblers, white-eyes, and flycatchers. These flocks are the key to racking up species quickly.' },
                  { time: '09:30 AM', title: 'Endemic Focus Habitats', desc: 'Target specific habitats for priority endemics. Mubwindi Swamp for African Green Broadbill (listen for the "sisisiiii" call), bamboo zones for Shelley\'s Crimsonwing, forest edges for Rwenzori Batis, and dense undergrowth for Kivu Ground-Thrush.' },
                  { time: '11:00 AM', title: 'Mid-Morning Break', desc: 'Pause for refreshments and review your checklist. Your guide shares ecological insights about each species and helps with visual and audio identification practice.' },
                  { time: '11:30 AM', title: 'Understory Specialists', desc: 'Work the forest understory for skulking species: Red-throated Alethe, Archer\'s Robin-Chat, illadopsis, and greenbuls. These require patience and expert ear-birding skills.' },
                  { time: '01:00 PM', title: 'Lunch & Conservation Talk', desc: 'Enjoy a packed lunch while discussing Bwindi\'s conservation success stories. Learn how birding tourism directly supports habitat protection and local communities.' },
                  { time: '02:00 PM', title: 'Afternoon Birding', desc: 'Continue birding along different elevation zones or trails. Afternoon light is excellent for photography, and many species remain active. Target any species missed in the morning.' },
                  { time: '03:30 PM', title: 'Final Target Species', desc: 'Make focused efforts for any remaining target endemics or lifers. Your guide knows the most reliable locations and will position you for the best views and photos.' },
                  { time: '04:30 PM', title: 'Return & Species Review', desc: 'Exit the forest, review your complete checklist, and celebrate your endemic count. Most full-day birders record 60-100+ species including 8-15 Albertine endemics—an exceptional day by any standard!' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time.split(' ')[0]}<br />{step.time.split(' ')[1]}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Essential Birding Gear & What to Bring
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Packing Note:</strong> Bwindi's trails can be steep, muddy, and challenging. Proper gear makes
                  a huge difference in your comfort and birding success. Your guide provides a spotting scope, but bring your own binoculars.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-binoculars" className="w-6 h-6" />
                    Optics & Reference Materials
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Binoculars:</strong> 8x42 or 10x42 recommended (good light-gathering for dark forest)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Field Guide:</strong> "Birds of East Africa" or "Birds of the Albertine Rift"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Camera:</strong> Telephoto lens (300mm+) for canopy species; image stabilization helps</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Notebook/App:</strong> For recording sightings and field notes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Voice Recorder:</strong> Optional but useful for documenting calls</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing & Footwear
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Waterproof hiking boots:</strong> Essential—trails are often muddy and slippery</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Rain jacket:</strong> Bwindi is a rainforest—pack waterproof layers even in dry season</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Long pants:</strong> Protect against stinging nettles and vegetation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Neutral/earth-toned clothing:</strong> Avoid bright colors that startle birds</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Layered clothing:</strong> Mornings are cool (10-15°C), afternoons warmer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Garden/leather gloves:</strong> Helpful when grabbing vegetation for balance</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-bottle-water" className="w-6 h-6" />
                    Essentials & Supplies
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Water:</strong> 1.5-2 liters for full-day birding</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Energy snacks:</strong> Trail mix, energy bars, fruit</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Insect repellent:</strong> Forest mosquitoes and other biting insects</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sunscreen & hat:</strong> For trail breaks in open areas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Small daypack:</strong> For carrying all your gear comfortably</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-lightbulb" className="w-6 h-6" />
                    Optional but Recommended
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Walking stick/trekking poles:</strong> Very helpful on steep sections</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Gaiters:</strong> Keep mud and moisture out of boots</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Extra batteries/power bank:</strong> For camera and devices</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Plastic bags:</strong> Keep electronics dry in rain</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Hand sanitizer/wet wipes:</strong> For cleaning hands during trail breaks</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={[
              { question: 'What are my realistic chances of seeing Albertine Rift endemics?', answer: 'Excellent! On a full-day birding safari with an experienced guide, you can realistically expect to encounter 8-15 of the 24 Albertine Rift endemics, depending on season, sector choice, and weather. Multi-day birding trips significantly increase your chances—serious birders often target 18-20+ endemics over 3-4 days. Key species like Great Blue Turaco, Rwenzori Batis, and various sunbirds are regularly seen. The African Green Broadbill requires more specialized effort (Mubwindi Swamp during May-October breeding season), while Shelley\'s Crimsonwing is genuinely rare and requires luck.' },
              { question: 'Which season offers the best birding?', answer: 'Bwindi offers excellent year-round birding, but timing affects different aspects. For maximum species diversity, the rainy seasons (March-May and September- November) are outstanding—breeding activity peaks, migratory species are present (November-April), and birds are highly active due to abundant food. However, trails become muddy and challenging. For easier trekking conditions with drier trails, the dry seasons (June-August and December-February) are more comfortable, though rain can occur anytime in this rainforest. May-June is particularly productive as it\'s the nesting period with abundant food sources. Serious birders often prefer the wetter seasons despite the challenging conditions because bird activity is simply spectacular.' },
              { question: 'Do I need a permit and what does it cost?', answer: 'Yes, all birding in Bwindi requires a Uganda Wildlife Authority (UWA) permit and must be done with a licensed guide. Current 2024-2025 fees are: Foreign non-residents: $30-40 per person, Foreign residents: $20-25 per person, and East African citizens: UGX 20,000-30,000 per person. Park entrance fees ($40 for foreign non-residents) are separate if birding is your primary activity and not part of a gorilla permit. These fees directly support park conservation, anti-poaching efforts, and community development programs. Permits must be obtained before entering the park, and we handle all permit arrangements for you.' },
              { question: 'Which sector should I choose for the best birding?', answer: 'Ruhija Sector in the eastern highlands is the undisputed birding champion, especially the famous Mubwindi Swamp Trail—it\'s challenging but offers the best shot at African Green Broadbill and the highest concentration of montane endemics. The Ruhija Nature Trail and Bamboo Zone add excellent diversity. Buhoma Sector offers easier access, beautiful trails (particularly the Buhoma Waterfall Trail), and excellent diversity with more comfortable trekking. Rushaga provides good southern forest birding. For serious endemic hunters, Ruhija is essential. For a more relaxed birding experience with great species diversity, Buhoma works wonderfully. We can also combine sectors over multiple days for the ultimate experience.' },
              { question: 'What fitness level do I need?', answer: 'Moderate fitness is recommended. Bwindi\'s trails are steep, muddy, often slippery, and at altitude (1,160-2,607m). A typical full-day birding safari involves 4-6 hours of walking with frequent stops for observation. You don\'t need to be an athlete, but you should be comfortable with uneven terrain and some elevation gain. The good news: birding pace is slow with many breaks for observations, so it\'s less strenuous than gorilla trekking. If you have specific fitness concerns, we can arrange shorter half-day excursions or select easier trails. Hiring a porter (available at trailheads) to carry your daypack makes a huge difference in comfort.' },
              { question: 'Can I combine birding with gorilla trekking?', answer: 'Absolutely—and many visitors do exactly this! It\'s a perfect combination that maximizes your Bwindi experience. A typical itinerary might be: Day 1 - Gorilla trekking (the bucket-list experience), Day 2 - Full-day birding safari (to see the forest\'s incredible avian diversity). This gives you both the mammals and birds, and helps justify the journey to this remote corner of Uganda. Gorilla permits are separate (current rates vary, contact us for details) and must be booked well in advance (3-6 months recommended). We can arrange combination packages that include both activities, accommodation, and all logistics.' },
              { question: 'Do I need to be an expert birder?', answer: 'Not at all! Your UWA-licensed guide is the expert—they\'re trained in both visual and audio identification and know exactly where to find target species. Beginners learn identification skills throughout the day, while experienced birders appreciate the guide\'s intimate knowledge of specific endemic locations and calls. Whether you\'re a hardcore lister chasing every endemic or a casual nature enthusiast who just loves beautiful birds, Bwindi delivers unforgettable experiences at every level. Your guide adapts the pace and focus to match your interests and experience.' }
            ]} title="Your Birding Questions Answered" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Bwindi Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/gorilla-trekking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1576669801775-ff43c5ab543d?w=800"
                      alt="Gorilla Trekking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Gorilla Trekking</h3>
                    <p className="text-gray-700 mb-4">The ultimate wildlife encounter.</p>
                    <p className="text-safari-gold font-bold">From $800</p>
                  </div>
                </Link>

                <Link to="/activities/bwindi-nature-walks" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800"
                      alt="Nature Walks"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Forest Nature Walks</h3>
                    <p className="text-gray-700 mb-4">Explore waterfalls and primates on foot.</p>
                    <p className="text-safari-gold font-bold">From $40</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> Half-day (4-5hrs) or Full-day (8-10hrs)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round; Mar-May, Sep-Nov peak</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Permit:</strong> $30-40 (foreign non-residents)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Moderate (steep, muddy trails)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Age:</strong> All ages welcome (fitness matters most)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-map-location-dot" className="w-6 h-6 text-safari-green" />
                    <span><strong>Sectors:</strong> Ruhija (best endemics), Buhoma, Rushaga</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Birding Safari
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Expert birding guide",
                    "Park entrance fees",
                    "Species checklist",
                    "Spotting scope use",
                    "Briefing"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Binoculars (bring own)",
                    "Field guide book",
                    "Transport to trail",
                    "Packed lunch (full day)",
                    "Tips"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Moderate. Steep/muddy trails.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Pace:</strong> Slow, with frequent stops.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Bwindi Birdwatching Safari"
      />
    </div>
  )
}
